const ALLOWED_TAGS = new Set([
  'A',
  'B',
  'BLOCKQUOTE',
  'BR',
  'DIV',
  'EM',
  'FIGCAPTION',
  'FIGURE',
  'FONT',
  'H2',
  'H3',
  'HR',
  'I',
  'IMG',
  'LI',
  'OL',
  'P',
  'SPAN',
  'STRONG',
  'U',
  'UL',
]);

const ALLOWED_IMAGE_HOSTS = new Set([
  'res.cloudinary.com',
]);

function sanitizeStyleValue(style = '') {
  const safeRules = [];
  for (const rule of String(style).split(';')) {
    const [rawProp, rawValue] = rule.split(':');
    if (!rawProp || !rawValue) continue;
    const prop = rawProp.trim().toLowerCase();
    const value = rawValue.trim();
    if (!value) continue;
    if (prop === 'color') {
      safeRules.push(`color: ${value}`);
    }
    if (prop === 'background-color') {
      safeRules.push(`background-color: ${value}`);
    }
    if (prop === 'font-size' && /^([0-9.]+(px|rem|em|%)|xx-small|x-small|small|medium|large|x-large|xx-large|smaller|larger)$/.test(value.toLowerCase())) {
      safeRules.push(`font-size: ${value.toLowerCase()}`);
    }
    if (prop === 'text-align' && ['left', 'center', 'right'].includes(value.toLowerCase())) {
      safeRules.push(`text-align: ${value.toLowerCase()}`);
    }
  }
  return safeRules.join('; ');
}

function isSafeUrl(value = '') {
  if (!value) return false;
  if (value.startsWith('/')) return true;
  try {
    const url = new URL(value);
    return ['http:', 'https:', 'mailto:'].includes(url.protocol);
  } catch {
    return false;
  }
}

function isSafeImageUrl(value = '') {
  if (!value) return false;
  if (value.startsWith('blob:')) return true;
  try {
    const url = new URL(value);
    return ['http:', 'https:'].includes(url.protocol) && ALLOWED_IMAGE_HOSTS.has(url.hostname);
  } catch {
    return false;
  }
}

export function sanitizeArticleHtml(html = '') {
  if (!html || typeof window === 'undefined' || typeof window.DOMParser === 'undefined') return '';

  const parser = new window.DOMParser();
  const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
  const root = doc.body.firstElementChild;
  if (!root) return '';

  function walk(node) {
    for (const child of [...node.childNodes]) {
      if (child.nodeType === Node.ELEMENT_NODE) {
        const tagName = child.tagName.toUpperCase();
        if (!ALLOWED_TAGS.has(tagName)) {
          child.replaceWith(...child.childNodes);
          continue;
        }

        for (const attr of [...child.attributes]) {
          const name = attr.name.toLowerCase();
          const value = attr.value;

          if (name.startsWith('on')) {
            child.removeAttribute(attr.name);
            continue;
          }

          if (name === 'style') {
            const safeStyle = sanitizeStyleValue(value);
            if (safeStyle) child.setAttribute('style', safeStyle);
            else child.removeAttribute('style');
            continue;
          }

          if (tagName === 'A' && name === 'href') {
            if (!isSafeUrl(value)) child.removeAttribute('href');
            else {
              child.setAttribute('target', '_blank');
              child.setAttribute('rel', 'noreferrer');
            }
            continue;
          }

          if (tagName === 'IMG' && name === 'src') {
            if (!isSafeImageUrl(value)) child.removeAttribute('src');
            continue;
          }

          if (['alt', 'title'].includes(name)) continue;
          if (tagName === 'FONT' && name === 'size' && /^[1-7]$/.test(value)) continue;

          child.removeAttribute(attr.name);
        }

        if (tagName === 'IMG' && !child.getAttribute('src')) {
          child.remove();
          continue;
        }
      }

      walk(child);
    }
  }

  walk(root);
  return root.innerHTML.trim();
}

export function extractFirstImageFromHtml(html = '') {
  const matched = String(html).match(/<img[^>]+src=["']([^"']+)["']/i);
  return matched?.[1] || '';
}

export function extractImageUrlsFromHtml(html = '') {
  const imageUrls = new Set();
  const matches = String(html).matchAll(/<img[^>]+src=["']([^"']+)["']/gi);
  for (const match of matches) {
    if (match?.[1]) {
      imageUrls.add(match[1]);
    }
  }
  return [...imageUrls];
}

export function convertPlainTextToHtml(text = '') {
  const lines = String(text || '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

  if (!lines.length) return '<p></p>';
  return lines.map((line) => `<p>${line}</p>`).join('');
}

export function extractPlainTextFromHtml(html = '') {
  const raw = String(html || '');
  if (!raw) return '';

  const normalized = raw
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|li|h2|h3|blockquote|figure)>/gi, '\n')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, '\'');

  return normalized
    .split('\n')
    .map((line) => line.replace(/\s+/g, ' ').trim())
    .filter(Boolean)
    .join('\n');
}

function decodeHtmlAttribute(value = '') {
  return String(value)
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, '\'')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&');
}

export function extractCloudinaryPublicIdFromUrl(url = '') {
  if (!url) return '';
  try {
    const parsed = new URL(url);
    if (parsed.hostname !== 'res.cloudinary.com') return '';
    const segments = parsed.pathname.split('/').filter(Boolean);
    const uploadIndex = segments.indexOf('upload');
    if (uploadIndex < 0) return '';
    let assetSegments = segments.slice(uploadIndex + 1);
    const versionIndex = assetSegments.findIndex((segment) => /^v\d+$/.test(segment));
    if (versionIndex >= 0) {
      assetSegments = assetSegments.slice(versionIndex + 1);
    }
    if (!assetSegments.length) return '';
    const lastSegment = assetSegments[assetSegments.length - 1];
    assetSegments[assetSegments.length - 1] = lastSegment.replace(/\.[a-z0-9]+$/i, '');
    return assetSegments.join('/');
  } catch {
    return '';
  }
}

export function extractCloudinaryPublicIdsFromHtml(html = '') {
  const publicIds = new Set();
  const imgTags = String(html).match(/<img\b[^>]*>/gi) || [];

  for (const tag of imgTags) {
    const explicit = tag.match(/\bdata-cloudinary-public-id=["']([^"']+)["']/i)?.[1];
    const src = tag.match(/\bsrc=["']([^"']+)["']/i)?.[1];
    const publicId = decodeHtmlAttribute(explicit || '') || extractCloudinaryPublicIdFromUrl(decodeHtmlAttribute(src || ''));
    if (publicId) {
      publicIds.add(publicId);
    }
  }

  return [...publicIds];
}

export function convertBlocksToHtml(blocks = []) {
  return blocks.map((block) => {
    const heading = block.heading ? `<h2>${block.heading}</h2>` : '';
    const body = block.body
      ? String(block.body)
        .split('\n')
        .filter(Boolean)
        .map((line) => `<p>${line}</p>`)
        .join('')
      : '';
    const caption = block.caption ? `<figcaption>${block.caption}</figcaption>` : '';

    if (block.type === 'gallery') {
      const images = (block.images || []).filter(Boolean).map((url) => `<img src="${url}" alt="${block.heading || '記事画像'}" />`).join('');
      return `${heading}<figure>${images}${caption}</figure>`;
    }

    if (block.type === 'image' || block.type === 'imageLeft' || block.type === 'imageRight') {
      const image = block.imageUrl ? `<figure><img src="${block.imageUrl}" alt="${block.heading || '記事画像'}" />${caption}</figure>` : '';
      return `${heading}${image}${body}`;
    }

    return `${heading}${body}`;
  }).join('');
}
